/**
 * A section of the node inspector that folds (remembered per environment by the `inspector`
 * service). An empty one shrinks to one "+ Add …" line, which opens it to add the first thing.
 */
import { Show, createSignal, type JSX } from "solid-js";
import { useApp } from "../../app/context";

/** A foldable section: its title and count, then its contents unless folded. */
export function Fold(props: {
  /** Its stable id (`links`), also `data-section` for checks. */
  id: string;
  title: string;
  /** How many things it holds, shown beside the title. */
  count?: number;
  /** Shrinks to the "+ Add …" line (`add`) until it is clicked. */
  empty?: boolean;
  /** What the "+" line says ("Add a comment"). */
  add?: string;
  children: JSX.Element;
}): JSX.Element {
  const { inspector } = useApp();
  /** Opened from the "+" line while still empty. */
  const [adding, setAdding] = createSignal(false);
  const shrunk = () => !!props.empty && !!props.add && !adding();
  return (
    <Show
      when={!shrunk()}
      fallback={
        <button type="button" class="fold-add" data-section-add={props.id} onClick={() => setAdding(true)}>
          + {props.add}
        </button>
      }
    >
      <section class="fold" data-section={props.id} data-folded={inspector.folded(props.id) ? "yes" : "no"}>
        <h3 class="fold-head">
          <button type="button" aria-expanded={!inspector.folded(props.id)} data-section-toggle={props.id} onClick={() => inspector.toggle(props.id)}>
            <span class="fold-arrow" aria-hidden="true">
              {inspector.folded(props.id) ? "▸" : "▾"}
            </span>
            {props.title}
            <Show when={props.count !== undefined}>
              <span class="fold-count">{props.count}</span>
            </Show>
          </button>
        </h3>
        <Show when={!inspector.folded(props.id)}>
          <div class="fold-body">{props.children}</div>
        </Show>
      </section>
    </Show>
  );
}
